import {Box, Button, Grid, Stack, Typography} from '@mui/material';
import {useState, useContext, useEffect} from 'react';
import APIServiceFormContext from '../../context/APIServiceFormContext';
import {iconColor, consistentSpacing} from './variables';

/**
 * Individual option of the MultipleOptionQuestion component
 *
 * @param {Object} props - Component props
 * @param {string} props.title
 * @param {string?} props.subtitle
 * @param {any} props.selected - useState value
 * @param {function} props.setSelected - useState setter
 * @param {FontAwesomeIcon?} props.startIcon
 * @param {any} props.width - width prop from material UI
 *
 * @returns {React.Component}
 */
const MultipleOptionQuestionButton = ({title, subtitle, selected, setSelected, startIcon, width}) => (
  <Button
    color={selected ? 'graydetailsselected' : 'graydetailsnotselected'}
    sx={{
      color: 'black',
      boxShadow: 'none',
      borderRadius: 3,
      fontWeight: 500,
      fontSize: 16,
      borderWidth: 3,
      px: {
        xs: 1,
        sm: 2,
      },
      py: 2,
      border: selected ? `3px solid ${iconColor}` : '3px solid transparent',
      width:
        width === undefined
          ? {
              xs: '100%',
              sm: '90%',
            }
          : width,
      gap: 2,
      justifyContent: startIcon === undefined ? 'center' : 'flex-start !important',
      '& .MuiButton-startIcon': {
        marginRight: '10px',
        marginLeft: '0',
      },
    }}
    size="medium"
    variant="contained"
    onClick={setSelected}
    startIcon={startIcon}
  >
    <Box
      sx={{
        py: 0.5,
      }}
    >
      <Typography fontWeight={450}>{title}</Typography>
      <Typography variant="body2" sx={{display: subtitle === undefined ? 'none' : 'normal'}}>
        {subtitle}
      </Typography>
    </Box>
  </Button>
);

/**
 * MultipleOptionQuestion
 * Renders a question title with multiple options to choose but only one to be selected. State is not being managed directly in this component, it should be done from the parent component. See this directory's README.md for more details.
 *
 * @param {Object} props - Component props
 * @param {string} props.title
 * @param {Object?} props.titleSX - sx prop from material UI that will be passed to the title component(optional)
 * @param {Array.<{
 *   title: string
 *   subtitle: string|null
 *   value: any
 *   icon: FontAwesomeIcon|null
 * }>} props.choices - options displayed in this question
 * @param {Number|Object|string} props.optionWidth - Width prop passed to the sx of each option present
 *
 * @returns {React.Component}
 */
export const MultipleOptionQuestion = ({title, titleSX, choices, optionWidth, apiName}) => {
  const [selected, setSelected] = useState(null);
  const {setValidationFunc, setSendDataArgs, savedValue} = useContext(APIServiceFormContext);
  const componentValidationWrapper = () => {
    function componentValidation() {
      if (selected === null) return 'Please select an option.';
      return '';
    }

    return componentValidation;
  };

  useEffect(() => {
    setSelected(savedValue === undefined ? null : savedValue);
  }, [savedValue]);
  useEffect(() => {
    setValidationFunc(componentValidationWrapper);
    setSendDataArgs({data: selected});
  }, [selected]);
  useEffect(() => setSelected(savedValue === undefined ? null : savedValue), [title]);

  return (
    <Grid container justifyContent="center" flexDirection="column" rowGap={consistentSpacing}>
      <Grid item xs={12}>
        <Typography
          component={'p'}
          variant="subtitle0"
          sx={{
            my: {xs: 0, sm: 3},
            textAlign: 'center',
            ...titleSX,
          }}
        >
          {title}
        </Typography>
      </Grid>
      <Grid item>
        <Stack direction="column" alignItems="center" justifyContent="center" spacing={0.5} sx={{pt: 1, rowGap: 2}}>
          {choices.map((choice, index) => (
            <MultipleOptionQuestionButton
              key={index}
              title={apiName === 'hehealth' ? choice.value : choice.title}
              subtitle={choice.subtitle}
              selected={apiName === 'hehealth' ? selected === choice : selected === choice.value}
              setSelected={() => {
                setSelected(apiName === 'hehealth' ? choice : choice.value);
              }}
              startIcon={choice.icon}
              width={optionWidth}
            />
          ))}
        </Stack>
      </Grid>
    </Grid>
  );
};
